import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { MaterialModule } from 'src/app/core/material.module';

/**
 * The app's single search bar. Every list page uses this so the field, the
 * clear button and the search icon look and behave the same everywhere.
 *
 * Works with reactive forms, with a plain value binding, or with a debounced
 * output for callers that fetch on every change:
 *   <app-search-field [formControl]="searchControl" placeholder="Search test" />
 *   <app-search-field [value]="filters.q" (valueChange)="search($event)" />
 *   <app-search-field [value]="filters.q" [debounce]="300"
 *                     (debouncedChange)="reload($event)" />
 */
@Component({
  selector: 'app-search-field',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './search-field.component.html',
  styleUrls: ['./search-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchFieldComponent),
      multi: true,
    },
  ],
})
export class SearchFieldComponent
  implements ControlValueAccessor, OnChanges, OnDestroy
{
  @Input() placeholder = 'Search';

  /** Debounce ms for `debouncedChange`. 0 = emit immediately on every change. */
  @Input() debounce = 0;

  /** Emits on every keystroke and when the field is cleared. */
  @Output() valueChange = new EventEmitter<string>();

  /** Emits once the typing settles, deduping consecutive identical values. */
  @Output() debouncedChange = new EventEmitter<string>();

  disabled = false;

  private text = '';
  private readonly inputSubject = new Subject<string>();
  private debounceSub?: Subscription;

  /**
   * Normalised on the way in: a null/undefined value would otherwise reach the
   * native input as-is and render the literal text "undefined".
   */
  @Input()
  set value(next: string | null | undefined) {
    this.text = next ?? '';
  }
  get value(): string {
    return this.text;
  }

  onTouched: () => void = () => undefined;
  private onChange: (value: string) => void = () => undefined;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['debounce']) {
      this.debounceSub?.unsubscribe();
      const ms = Math.max(0, this.debounce || 0);
      this.debounceSub = this.inputSubject
        .pipe(debounceTime(ms), distinctUntilChanged())
        .subscribe((value) => this.debouncedChange.emit(value));
    }
  }

  ngOnDestroy(): void {
    this.debounceSub?.unsubscribe();
  }

  writeValue(value: string | null | undefined): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(value: string): void {
    this.value = value;
    this.onChange(value);
    this.valueChange.emit(value);
    this.inputSubject.next(value);
  }

  clear(): void {
    this.onInput('');
  }
}
